import React, { useMemo, useState } from "react";
import { ArrowRight, Calculator } from "@phosphor-icons/react";

// Sector multiples calibrated to the Clinq Valuation Methodology v1.0 spec —
// Section 6 sector reference table. African discount is the markdown vs.
// global benchmarks (reflects illiquidity, thinner exit market, regulatory
// uncertainty) applied to the global median to set the sector base.
const SECTORS = {
  fintech: { label: "Fintech / Digital Financial Services", rev: [4, 8], evebitda: [12, 20], africa: 15 },
  agritech: { label: "Agritech / Agriculture", rev: [1.5, 3], evebitda: [6, 10], africa: 10 },
  healthtech: { label: "Health tech / Healthcare", rev: [3, 6], evebitda: [10, 16], africa: 15 },
  edtech: { label: "Edu tech / Education", rev: [2, 5], evebitda: [8, 14], africa: 20 },
  ecommerce: { label: "E-commerce / Retail tech", rev: [1.5, 3], evebitda: [5, 10], africa: 20 },
  logistics: { label: "Logistics / Supply chain", rev: [1.5, 3], evebitda: [6, 10], africa: 15 },
  saas: { label: "SaaS / Enterprise software", rev: [4, 10], evebitda: [15, 25], africa: 20 },
  cleantech: { label: "CleanTech / Energy", rev: [2, 4], evebitda: [8, 14], africa: 10 },
  manufacturing: { label: "Manufacturing / Industrials", rev: [0.8, 1.5], evebitda: [4, 7], africa: 10 },
  services: { label: "Professional services", rev: [0.5, 1.2], evebitda: [3, 6], africa: 10 },
  proptech: { label: "Proptech / Real estate tech", rev: [2, 4], evebitda: [8, 14], africa: 15 },
  media: { label: "Media & content", rev: [1, 3], evebitda: [5, 10], africa: 20 },
  insurtech: { label: "Insurtech", rev: [3, 7], evebitda: [10, 16], africa: 15 },
  marketplace: { label: "Marketplace / Platform", rev: [3, 7], evebitda: [10, 18], africa: 15 },
  other: { label: "Other / Diversified", rev: [1.5, 3.5], evebitda: [5, 10], africa: 15 },
};

// Revenue Quality factor — Section 5.1.2 of the methodology.
// Maps user inputs → Low (0.6x) / Medium (1.0x) / High (1.4x) multiplier
// against the sector base.
const QUALITY_FACTOR = { low: 0.6, medium: 1.0, high: 1.4 };

const C = {
  ink: "#0A2826",
  panel: "#103532",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  amber: "#FCD34D",
};

export default function ValuationCalculator({ onRequest }) {
  // Stage toggle — methodology has very different methods for pre/post revenue
  const [stage, setStage] = useState("post"); // 'post' | 'pre'

  // Post-revenue inputs (Section 5)
  const [revenue, setRevenue] = useState("");
  const [growth, setGrowth] = useState("");
  const [grossMargin, setGrossMargin] = useState("");
  const [topCustomers, setTopCustomers] = useState("");
  const [revType, setRevType] = useState("recurring"); // recurring | mixed | oneoff
  const [sector, setSector] = useState("");

  // Pre-revenue inputs — Berkus milestones (Section 4.2)
  const [milestones, setMilestones] = useState({
    sound_idea: false,
    working_mvp: false,
    experienced_team: false,
    early_users: false,
    revenue_partnership: false,
  });

  const [submitted, setSubmitted] = useState(false);

  // -------- Calculate the revenue-quality factor (post-revenue) ----------
  const qualityScore = useMemo(() => {
    if (stage !== "post") return null;
    const gm = parseFloat(grossMargin);
    const tc = parseFloat(topCustomers);
    const g = parseFloat(growth);
    if (isNaN(gm) || isNaN(tc) || isNaN(g)) return null;

    // Methodology rubric — each factor scores Low (1), Medium (2), High (3).
    const f1 = gm < 30 ? 1 : gm <= 55 ? 2 : 3;             // Gross margin
    const f2 = tc >= 60 ? 1 : tc >= 30 ? 2 : 3;            // Customer concentration
    const f3 = revType === "oneoff" ? 1 : revType === "mixed" ? 2 : 3; // Revenue type
    const f4 = g < 20 ? 1 : g <= 60 ? 2 : 3;               // YoY growth

    const avg = (f1 + f2 + f3 + f4) / 4;
    const band = avg <= 1.65 ? "low" : avg < 2.4 ? "medium" : "high";
    return { band, avg, factors: { gross_margin: f1, concentration: f2, revenue_type: f3, growth: f4 } };
  }, [stage, grossMargin, topCustomers, revType, growth]);

  // -------- Compute the three-value range ----------
  const result = useMemo(() => {
    if (stage === "post") {
      const r = parseFloat(revenue);
      const s = SECTORS[sector];
      if (!r || r <= 0 || !s || !qualityScore) return null;
      // Apply African discount to global multiple
      const lowBase = s.rev[0] * (1 - s.africa / 100);
      const highBase = s.rev[1] * (1 - s.africa / 100);
      const midBase = (lowBase + highBase) / 2;
      const qf = QUALITY_FACTOR[qualityScore.band];
      // Conservative: lowBase × Low quality (0.6)
      // Base: midBase × user's quality factor
      // Optimistic: highBase × High quality (1.4)
      return {
        method: `Revenue Multiple — ${s.label}`,
        conservative: { ev: r * lowBase * QUALITY_FACTOR.low, mult: lowBase * QUALITY_FACTOR.low },
        base: { ev: r * midBase * qf, mult: midBase * qf },
        optimistic: { ev: r * highBase * QUALITY_FACTOR.high, mult: highBase * QUALITY_FACTOR.high },
        africaDiscount: s.africa,
        sectorBaseLow: s.rev[0],
        sectorBaseHigh: s.rev[1],
        qualityBand: qualityScore.band,
      };
    }
    // Pre-revenue — Berkus
    // Methodology Section 4.2 — value bands per milestone (in KES; convert to USD at ~130).
    const KES_PER_USD = 130;
    const BERKUS = {
      sound_idea: [4_000_000, 8_000_000],
      working_mvp: [5_000_000, 10_000_000],
      experienced_team: [5_000_000, 12_000_000],
      early_users: [4_000_000, 8_000_000],
      revenue_partnership: [5_000_000, 12_000_000],
    };
    const ticked = Object.entries(milestones).filter(([, v]) => v);
    if (ticked.length === 0) return null;
    let lowKes = 0, highKes = 0;
    ticked.forEach(([k]) => { lowKes += BERKUS[k][0]; highKes += BERKUS[k][1]; });
    const midKes = (lowKes + highKes) / 2;
    return {
      method: "Berkus Method — Pre-revenue milestones",
      conservative: { ev: lowKes / KES_PER_USD, mult: null },
      base: { ev: midKes / KES_PER_USD, mult: null },
      optimistic: { ev: highKes / KES_PER_USD, mult: null },
      qualityBand: null,
      milestonesAchieved: ticked.length,
    };
  }, [stage, revenue, sector, qualityScore, milestones]);

  const fmt = (n) => {
    if (n >= 1_000_000) return `USD ${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `USD ${(n / 1_000).toFixed(0)}k`;
    return `USD ${Math.round(n)}`;
  };

  const onCalculate = () => setSubmitted(true);

  const canCalc = stage === "post"
    ? (revenue && sector && growth !== "" && grossMargin !== "" && topCustomers !== "")
    : Object.values(milestones).some(Boolean);

  return (
    <div className="rounded-2xl p-7 sm:p-9 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${C.panel} 0%, ${C.ink} 70%)`,
        border: "1px solid rgba(252, 211, 77, 0.15)",
      }}
      data-testid="valuation-calculator">

      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md"
            style={{ backgroundColor: `${C.amber}22`, color: C.amber }}>
            <Calculator size={14} weight="bold" />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: C.amber }}>
            Indicative calculator · Clinq methodology v1
          </span>
        </div>
        {/* Stage toggle */}
        <div className="inline-flex rounded-md overflow-hidden border" style={{ borderColor: "rgba(252,211,77,0.25)" }}>
          {[
            { id: "post", label: "Post-revenue" },
            { id: "pre", label: "Pre-revenue" },
          ].map((s) => (
            <button key={s.id} onClick={() => { setStage(s.id); setSubmitted(false); }}
              data-testid={`valuation-stage-${s.id}`}
              className="text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 transition-colors"
              style={{
                backgroundColor: stage === s.id ? C.amber : "transparent",
                color: stage === s.id ? "#000" : "rgba(255,255,255,0.55)",
              }}>{s.label}</button>
          ))}
        </div>
      </div>
      <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-white tracking-tight leading-tight">
        {stage === "post"
          ? <>Get an indicative range — <span style={{ color: C.amber }}>three-value style</span>.</>
          : <>Pre-revenue? Score by <span style={{ color: C.amber }}>Berkus milestones</span>.</>}
      </h3>
      <p className="text-[14px] sm:text-[15px] leading-relaxed mt-3 max-w-3xl"
        style={{ color: "rgba(255,255,255,0.65)" }}>
        {stage === "post"
          ? "This tool uses Clinq's valuation methodology—which accounts for industry revenue trends, revenue reliability, and African market conditions."
          : "Tick the milestones you've hit. We'll apply the Berkus value bands localised for African markets (KES 4–12M per milestone) and convert to a USD range."}
      </p>

      {/* INPUTS */}
      {stage === "post" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-7">
          <Field label="TTM revenue (USD)" hint="Trailing 12 months, gross">
            <input type="number" min="0" placeholder="e.g. 500000"
              value={revenue} onChange={(e) => setRevenue(e.target.value)}
              data-testid="valuation-calc-revenue"
              className="w-full px-3 py-2.5 rounded-md text-[15px] font-mono-num text-right focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
          </Field>
          <Field label="YoY growth (%)" hint="Last year vs prior">
            <input type="number" placeholder="e.g. 35"
              value={growth} onChange={(e) => setGrowth(e.target.value)}
              data-testid="valuation-calc-growth"
              className="w-full px-3 py-2.5 rounded-md text-[15px] font-mono-num text-right focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
          </Field>
          <Field label="Gross margin (%)" hint="(Rev − COGS) ÷ Rev">
            <input type="number" placeholder="e.g. 45"
              value={grossMargin} onChange={(e) => setGrossMargin(e.target.value)}
              data-testid="valuation-calc-margin"
              className="w-full px-3 py-2.5 rounded-md text-[15px] font-mono-num text-right focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
          </Field>
          <Field label="Top-3 customer concentration (%)" hint="Share of revenue from top 3">
            <input type="number" placeholder="e.g. 35"
              value={topCustomers} onChange={(e) => setTopCustomers(e.target.value)}
              data-testid="valuation-calc-concentration"
              className="w-full px-3 py-2.5 rounded-md text-[15px] font-mono-num text-right focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
          </Field>
          <Field label="Revenue type" hint="Methodology-defined">
            <select value={revType} onChange={(e) => setRevType(e.target.value)}
              data-testid="valuation-calc-revtype"
              className="w-full px-3 py-2.5 rounded-md text-[14px] focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}>
              <option value="recurring" style={{ backgroundColor: C.ink }}>Recurring / subscription</option>
              <option value="mixed" style={{ backgroundColor: C.ink }}>Mixed</option>
              <option value="oneoff" style={{ backgroundColor: C.ink }}>One-off / project</option>
            </select>
          </Field>
          <Field label="Sector" hint="Closest match">
            <select value={sector} onChange={(e) => setSector(e.target.value)}
              data-testid="valuation-calc-sector"
              className="w-full px-3 py-2.5 rounded-md text-[14px] focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}>
              <option value="" style={{ backgroundColor: C.ink }}>— pick a sector —</option>
              {Object.entries(SECTORS).map(([key, val]) => (
                <option key={key} value={key} style={{ backgroundColor: C.ink }}>{val.label}</option>
              ))}
            </select>
          </Field>
        </div>
      ) : (
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ["sound_idea", "Sound idea · validated problem-solution fit", "KES 4–8M"],
            ["working_mvp", "Working prototype / MVP built", "KES 5–10M"],
            ["experienced_team", "Experienced team in place", "KES 5–12M"],
            ["early_users", "Signed LOIs / pilots / early users", "KES 4–8M"],
            ["revenue_partnership", "Revenue or formal partnership signed", "KES 5–12M"],
          ].map(([k, label, value]) => (
            <label key={k} className="flex items-start gap-3 p-3 rounded-md cursor-pointer transition-colors"
              style={{
                backgroundColor: milestones[k] ? "rgba(252, 211, 77, 0.06)" : "rgba(0,0,0,0.35)",
                border: `1px solid ${milestones[k] ? `${C.amber}55` : "rgba(255,255,255,0.08)"}`,
              }}
              data-testid={`valuation-milestone-${k}`}>
              <input type="checkbox" checked={milestones[k]}
                onChange={(e) => setMilestones((p) => ({ ...p, [k]: e.target.checked }))}
                className="mt-1 accent-amber-400" />
              <div>
                <div className="text-white font-bold text-[14px]">{label}</div>
                <div className="text-[11px] mt-0.5 font-mono-num" style={{ color: "rgba(255,255,255,0.5)" }}>{value}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      <button onClick={onCalculate}
        disabled={!canCalc}
        data-testid="valuation-calc-submit"
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-extrabold transition-colors disabled:opacity-40"
        style={{ backgroundColor: C.amber, color: "#000" }}>
        {stage === "post" ? "Calculate three-value range" : "Score my Berkus valuation"} <ArrowRight size={14} weight="bold" />
      </button>

      {/* RESULT */}
      {submitted && result && (
        <div className="mt-7 pt-6 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="valuation-calc-result">
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: C.tealSoft }}>
            {result.method}
          </div>

          {/* Three-value table */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {[
              { key: "conservative", label: "Conservative", color: "#94A3B8" },
              { key: "base", label: "Base case", color: C.amber },
              { key: "optimistic", label: "Optimistic", color: "#9AE6B4" },
            ].map((c) => (
              <div key={c.key} className="rounded-lg p-4"
                style={{ backgroundColor: "rgba(0,0,0,0.35)", border: `1px solid ${c.color}33` }}
                data-testid={`valuation-result-${c.key}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: c.color }}>
                  {c.label}
                </div>
                <div className="font-display font-black mt-2" style={{ fontSize: "1.65rem", letterSpacing: "-0.02em" }}>
                  {fmt(result[c.key].ev)}
                </div>
                {result[c.key].mult !== null && (
                  <div className="text-[10px] mt-1 font-mono-num" style={{ color: "rgba(255,255,255,0.45)" }}>
                    {result[c.key].mult.toFixed(2)}× revenue
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Why these numbers */}
          {stage === "post" && (
            <div className="rounded-md p-4 mb-5"
              style={{ backgroundColor: "rgba(123, 211, 206, 0.05)", border: "1px solid rgba(123, 211, 206, 0.15)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-2" style={{ color: C.tealSoft }}>
                How we got there
              </div>
              <ul className="text-[12px] space-y-1.5" style={{ color: "rgba(255,255,255,0.7)" }}>
                <li>· Sector base multiple: <span className="font-mono-num">{result.sectorBaseLow}× – {result.sectorBaseHigh}×</span> revenue</li>
                <li>· African market discount applied: <span className="font-mono-num">−{result.africaDiscount}%</span></li>
                <li>· Your revenue quality band: <span className="font-bold capitalize" style={{ color: C.amber }}>{result.qualityBand}</span> → multiplier <span className="font-mono-num">{QUALITY_FACTOR[result.qualityBand]}×</span></li>
              </ul>
            </div>
          )}

          <p className="text-[12px] leading-relaxed max-w-3xl"
            style={{ color: "rgba(255,255,255,0.55)" }}>
            This range comes from one method only. A defensible Clinq valuation triangulates this with DCF,
            comparable transactions, and (where relevant) EV/EBITDA or NAV — and produces an auditable report
            that traces every figure to your financial model.
          </p>
          <button onClick={onRequest}
            data-testid="valuation-calc-request"
            className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold transition-colors"
            style={{ backgroundColor: C.orange, color: "#000" }}>
            Get the full triangulated report <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>
        {label}
      </div>
      {children}
      <div className="text-[10px] mt-1.5" style={{ color: "rgba(255,255,255,0.35)" }}>{hint}</div>
    </label>
  );
}
